import React, { useMemo, useState, useContext, useEffect } from "react";
import { Modal, Button, Accordion, Table } from "react-bootstrap";
import SmuTable from "../../utils/smu-table/SmuTable";
import { fetchSkirtsByCampaignId, previewXlsxSkirts, previewUploadFaldonXlsx } from "../../api/files";
import SmuPdfPreview from "../../utils/smu-pdf-preview/SmuPdfPreview";
import { StoreContext } from "../../store/StoreContext";
import { IconAdd, IconCheck, IconEqual, IconPencilEdit, IconPlus, IconX } from "../../icons/Icon";
import SmuLoading from "../../utils/smu-loading/SmuLoading";
import { COLOR_PRIMARY } from "../../constants/colors";

const Stepper = ({ active }) => {
  const steps = ["Datos generales", "Vista documento", "Previsualización"];
  return (
    <div className="d-flex align-items-center justify-content-center mb-3">
      {steps.map((label, idx) => (
        <div key={label} className="d-flex align-items-center justify-content-center">
          <div
            className={`px-3 py-2 rounded text-white ${
              idx < active ? "bg-success" : idx === active ? "" : "bg-secondary"
            }`}
            style={{ whiteSpace: "nowrap", ...(idx === active ? { backgroundColor: "#013E8F" } : {}) }}
          >
            {label}
          </div>
          {idx < steps.length - 1 && (
            <div className="mx-2" style={{ width: 64, height: 2, background: "#bbb" }} />
          )}
        </div>
      ))}
    </div>
  );
};

const PreviewXlsxModal = ({ show, onHide, parsed = [], files = [], handleUploadProcess, calculateCategoryMenu }) => {
  const { loggedUser, promotions } = useContext(StoreContext);
  const FALDON_TABLE_SCROLL_HEIGHT = "420px";
  const [activeStep, setActiveStep] = useState(0);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewKey, setPreviewKey] = useState(null);
  const [loadingPreviewKey, setLoadingPreviewKey] = useState("null");
  const [filesToUpload, setFilesToUpload] = useState([]);
  const [canContinue, setCanContinue] = useState(false);
  // Backend skirts fetched per campaignId
  const [backendByFile, setBackendByFile] = useState({});
  const [loadingCampaigns, setLoadingCampaigns] = useState(false);

  const normalizeFormatValue = (value) => String(value ?? "").trim().toLowerCase();
  const normalizeKeyValue = (value) => String(value ?? "").trim().toLowerCase();
  const getCampaignFormatKey = (campaign, format, fileName) =>
    `${normalizeKeyValue(campaign)}__${normalizeFormatValue(format)}__${normalizeKeyValue(fileName)}`;

  useEffect(() => {
    setActiveStep(0); // Reset active step to 0 when modal is shown
    setFilesToUpload(files);
    if(!show) {
      setCanContinue(false);
    } else {
      const valid = validatePromotionalCampaigns();
      setCanContinue(valid);
    }
  }, [show]);

  useEffect(() => {
    const valid = validatePromotionalCampaigns();
    setCanContinue(valid);
  }, [parsed, promotions]);

  const generalColumns = useMemo(
    () => [
      {
        name: "Nombre",
        selector: (row) => row.fileName,
        sortable: true,
        cell: (row) => <div className="cell text-truncate text-wrap" title={row.fileName}>{row.fileName}</div>,
        grow: 2,
      },
      {
        name: "Fecha inicio - fin",
        selector: (row) => `${row.header.startDate} - ${row.header.endDate}`,
        cell: (row) => (
          <div className="cell">
            {row.header.startDate
              ? new Date(row.header.startDate).toLocaleDateString("es-CL", {
                  day: "2-digit",
                  month: "2-digit",
                  year: "numeric"
                })
              : ""}
            {" - "}
            {row.header.endDate
              ? new Date(row.header.endDate).toLocaleDateString("es-CL", {
                  day: "2-digit",
                  month: "2-digit",
                  year: "numeric"
                })
              : ""}
          </div>
        ),
        sortable: true,
        width: "180px",
      },
      {
        name: "Campaña (id + nombre)",
        selector: (row) => row.header.campaign,
        cell: (row) => 
        (
          validatePromotionalCampaign(row.header.campaign, loggedUser.username) ? (
              <div className="cell text-truncate text-wrap" title="Campaña válida">{row.header.campaign || "N/A"} - {row.header.campaignName || "N/A"} - <IconCheck /></div>
            ) : (
              <div className="cell text-truncate text-wrap" title="Campaña no válida" style={{ color: {COLOR_PRIMARY} }}>{row.header.campaign || "N/A"} - {row.header.campaignName || "N/A"} - <IconX /></div>
            )
        )
      },
      {
        name: "Formato impresión",
        selector: (row) => row.header.format,
        cell: (row) => <div className="cell">{row.header.format || "N/A"}</div>,
        sortable: true,
        width: "150px",
      },
      {
        name: "Texto legal",
        selector: (row) => row.header.legalText,
        cell: (row) => <div className="cell" style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }} title={row.header.legalText}>{row.header.legalText || ""}</div>,
        sortable: false,
        width: "150px",
      },
    ],
    [parsed, promotions, loggedUser?.username]
  );
  
  const columnDefsFaldon = {
    faldon: "Faldon",
    formatoImpresion: "Formato impresión",
    negocio: "Negocio",
    textoLegal: "Texto legal",
    legal: "Legal forzado",
    producto: "Producto",
    marca: "Marca",
    categoria: "Categoría",
    ean13: "EAN 13",
    precioModal: "Precio modal",
    precioOfertaUnitario: "Precio oferta unitario",
    precioOfertaUnitario2: "Precio oferta unitario 2",
    precioOfertaUnitario3: "Precio oferta unitario 3",
    factoresDeVenta: "Factores de venta",
    factoresDeVenta2: "Factores de venta 2",
    factoresDeVenta3: "Factores de venta 3",
    precioOfertaTotalXFactor: "Precio oferta total x factor",
    precioF2: "Precio F2",
    precioF3: "Precio F3",
    nombreMecanica: "Nombre mecanica",
    ahorras: "Ahorras",
    nombreFaldon: "Nombre faldon",
    descripcionFaldon: "Descripción faldon",
    unidad: "Unidad",
    porcentaje: "Porcentaje",
    textoA: "Texto A",
    textoB: "Texto B",
    textoC: "Texto C",
    textoD: "Texto D",
    ppum: "PPUM",
    umvPpum: "UMV PPUM",
    textoLegal: "Texto legal",
    nombreCampanaProd: "Campaña",
    drenado: "Drenado",
    ppumModal: "PPUM MODAL",
    ppumOferta2: "PPUM OFERTA 2",
    ppumOferta3: "PPUM OFERTA 3",
    futuroViral1: "FUTURO VIRAL 1",
    futuroViral2: "FUTURO VIRAL 2",
    futuroViral3: "FUTURO VIRAL 3",
    futuroViral4: "FUTURO VIRAL 4",
    futuroViral5: "FUTURO VIRAL 5",
  };
  const faldonColumns = useMemo(() => {
    const baseCols = Object.keys(columnDefsFaldon).map((key) => ({
      name: (
        <span title={columnDefsFaldon[key]}>
          {columnDefsFaldon[key]}
        </span>
      ),
      selector: (row) => row[key],
      sortable: true,
      cell: (row) => <div className="cell" style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }} title={row[key]}>{row[key]}</div>,
      width: "150px",
    }));
    // Modificar columna "compareResult" para que muestre un icono de acuerdo al resultado de la comparación
    const estadoCol = {
      name: "Estado",
      selector: (row) => row.compareResult,
      sortable: true,
      cell: (row) => (
        row.compareResult == "default" ? (
          <div className="cell" style={{ whiteSpace: "normal" }} title="Sin cambios"><IconEqual color={COLOR_PRIMARY} /></div>
        ) : row.compareResult == "new" ? (
          <div className="cell" style={{ whiteSpace: "normal" }} title="Nuevo"><IconAdd color={COLOR_PRIMARY} /></div>
        ) : (
          <div className="cell" style={{ whiteSpace: "normal" }} title="Modificado"><IconPencilEdit color={COLOR_PRIMARY} /></div>
        )
      ),
      width: "100px",
    };
    return [estadoCol, ...baseCols];
  }, []);

  // Fetch backend skirts for each campaign when entering step 2 (and when parsed/user changes)
  useEffect(() => {
    if (activeStep !== 1) return;
    if (filesToUpload.length === 0) return;

    let mounted = true;
    setLoadingCampaigns(true);
    Promise.all(
      filesToUpload.map((file) =>
        previewUploadFaldonXlsx(file, loggedUser.username, parsed.find((p) => p.fileName === file.name).rows, parsed.find((p) => p.fileName === file.name).header.campaign, calculateCategoryMenu(file.name)).then(({ data, error }) => ({
          data: error ? [] : (data || []),
          fileName: file.name,
          campaign: parsed.find((p) => p.fileName === file.name).header.campaign,
          format: parsed.find((p) => p.fileName === file.name).header.format,
        }))
      )
    ).then((results) => {
      if (!mounted) return;
      const backendByFile = {};
      results.forEach(({ data, fileName, campaign, format }) => {
        const campaignFormatKey = getCampaignFormatKey(campaign, format, fileName);
        backendByFile[campaignFormatKey] = Array.isArray(data) ? data : [];
      });
      setBackendByFile(backendByFile);
    }).catch(() => {
      if (!mounted) return;
      setBackendByFile({});
    }).finally(() => {
      if (!mounted) return;
      setLoadingCampaigns(false);
    });
    return () => { mounted = false; };
  }, [activeStep, parsed, loggedUser?.username]);

  // Build compared rows per campaign (pure)
  const comparedRowsByCampaign = useMemo(() => {
    const map = {};
    parsed.forEach((p) => {
      const campaignFormatKey = getCampaignFormatKey(p?.header?.campaign, p?.header?.format, p?.fileName);
      const backendRows = backendByFile[campaignFormatKey] || [];
      map[campaignFormatKey] = buildComparedRows(p.rows, backendRows);
    });
    return map;
  }, [backendByFile]);

  // Normaliza valores para comparación robusta (ignora tipos y formatos comunes)
  function normalize(value) {
    if (value == null) return "";
    if (value instanceof Date && !isNaN(value)) return formatDate(value);
    if (typeof value === "string") {
      const s = value.trim();
      const sNoSpaces = s.replace(/\s+/g, "");
      const parsedDate = parseDateLike(s);
      if (parsedDate) return formatDate(parsedDate);
      // Maneja miles con separadores "." o "," (ej: "5,750" o "5.750" -> "5750")
      if (/^\d{1,3}([.,]\d{3})+$/.test(sNoSpaces)) {
        const withoutGroupSep = sNoSpaces.replace(/[.,]/g, "");
        return String(Number(withoutGroupSep));
      }
      // Canonicaliza números simples (sin separadores de miles con coma)
      if (/^[+-]?\d+(\.\d+)?$/.test(sNoSpaces)) return String(Number(sNoSpaces));
      return s;
    }
    if (typeof value === "boolean") return value ? "true" : "false";
    if (typeof value === "number") return String(value);
    return String(value);
  }

  function parseDateLike(s) {
    const m = s.match(/^(\d{1,2})[-\/](\d{1,2})[-\/](\d{4})$/);
    if (!m) return null;
    const dd = Number(m[1]);
    const mm = Number(m[2]);
    const yyyy = Number(m[3]);
    const d = new Date(yyyy, mm - 1, dd);
    return d.getFullYear() === yyyy && d.getMonth() === mm - 1 && d.getDate() === dd ? d : null;
  }

  function formatDate(d) {
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const yyyy = d.getFullYear();
    return `${dd}-${mm}-${yyyy}`;
  }

  /*
    Funcion que compara la información del faldón actual con la información del 
    faldón asociado a la campaña dentro de la base de datos, agrega el index a cada item 
    "compareResult" con los siguientes valores: "default", "new", "updated"
  */
  function buildComparedRows(rows, backendRows) {
    // const backendByKey = new Map(
    //   (Array.isArray(backendRows) ? backendRows : []).map((item) => {
    //     const key = `${String(item?.faldon ?? "")}-${String(item?.ean13 ?? "")}-${String(item?.formatoImpresion ?? "")}`;
    //     return [key, item];
    //   })
    // );
    const result = (Array.isArray(rows) ? rows : []).map((r) => ({
      ...r,
      compareResult: "default",
    }));
    for (let i = 0; i < result.length; i++) {
      // Check inside backendByFile if the result[i].faldon exists on the values "faldonId_BD" or "faldonID_Excel"
      const item = result[i];
      const backendRow = backendRows.find((r) =>
        (r.EAN && item.ean13 && r.EAN.toLowerCase().includes(String(item.ean13).toLowerCase()))
      );
      if (backendRow && backendRow.TIPO == "CREAR") {
        item.compareResult = "new";
      } else if (backendRow && backendRow.TIPO == "ACTUALIZACION") {
        item.compareResult = "updated";
      }
      // const item = result[i];
      // const key = `${String(item?.faldon ?? "")}-${String(item?.ean13 ?? "")}-${String(item?.formatoImpresion ?? "")}`;
      // const dbItem = backendByKey.get(key);
      // if (!dbItem) {
      //   item.compareResult = "new";
      //   item.version = 1;
      //   continue;
      // } else {
      //   item.version = (parseInt(dbItem.version) ?? 1);
      // }
      // const keysToCompare = Object.keys(item).filter(
      //   (k) => k !== "faldon" && k !== "ean13" && k !== "formatoImpresion" && k !== "compareResult" && k !== "version"
      // );
      // for (let j = 0; j < keysToCompare.length; j++) {
      //   const k = keysToCompare[j];
      //   const uploadedVal = item[k] ?? "";
      //   const dbVal = dbItem[k] ?? "";
      //   if (normalize(uploadedVal) !== normalize(dbVal)) {
      //     item.compareResult = "updated";
      //     item.version += 1;
      //     break;
      //   }
      // }
    }
    return result;
  }

  

  function validatePromotionalCampaign(campaign) {
    if(promotions.length === 0) {
      return false;
    } else {
      var valid = promotions.find((p) => p.ID_PROMO === campaign);
      return valid ? true : false;
    }
  }

  function validatePromotionalCampaigns() {
    if(parsed.length === 1) {
      var valid = validatePromotionalCampaign(parsed[0].header.campaign);
      return valid;
    } else {
      var valid = parsed.every((p) => validatePromotionalCampaign(p.header.campaign)); 
      return valid;
    }
  }
  if (!parsed.length) {return null;}
  const renderStep1 = () => (
    <div>
      <SmuTable columns={generalColumns} data={parsed} loading={false} />
    </div>
  );

  const renderStep2 = () => (
    <Accordion alwaysOpen>
      {parsed.map((p, idx) => {
        const title = `${p.fileName} (${p.header.campaign || "N/A"} - ${p.header.campaignName || "N/A"})`;
        const campaignFormatKey = getCampaignFormatKey(p?.header?.campaign, p?.header?.format, p?.fileName);
        
        // --- LOCAL SUMMARY & TOOLTIP ---
        const articles = comparedRowsByCampaign[campaignFormatKey] || [];
        const backendArticles = backendByFile[campaignFormatKey] || [];
        const sideArticles = backendArticles.filter(
          (r) => r.TIPO === "CREAR" && r.FALDONID_BD?.includes("-tmp-")
        );
        const localTooltip =
          sideArticles.length > 0
            ? "EAN asociados que seguiran siendo vigentes: \n" +
              sideArticles.map((r) => r.EAN).join(", ")
            : "";
        const uniqueBackendSkirts = new Set(backendArticles.map((r) => r.FALDONID_BD)).size;
        const unchangedArticles = articles.filter((r) => r.compareResult === "default");

        let localSummary = "Resumen: ";
        if (unchangedArticles.length === articles.length && articles.length > 0) {
          localSummary = "No existen cambios con los artículos ya publicados";
        } else {
          localSummary = `Se subirán ${backendArticles.length} artículos en ${uniqueBackendSkirts} faldones.`;
          if (sideArticles.length > 0) {
            localSummary += ` (esto incluye independizar/anexar otros ${sideArticles.length} artículos vigentes de versiones anteriores)`;
          }
        }
        // --- END LOCAL CALC ---

        return (
          <Accordion.Item eventKey={String(idx)} key={p.fileName}>
            <Accordion.Header>{title}</Accordion.Header>
            <Accordion.Body>
              {loadingCampaigns ? (
                <div className="d-flex align-items-center justify-content-center my-3">
                  <SmuLoading label="Cargando datos de campaña..." />
                </div>
              ) : (
                <SmuTable
                  columns={faldonColumns}
                  data={articles}
                  loading={false}
                  fixedHeader
                  fixedHeaderScrollHeight={FALDON_TABLE_SCROLL_HEIGHT}
                />
              )}
            </Accordion.Body>
            {!loadingCampaigns && (
              <div className="mb-3 m-4 mt-0" style={{ borderLeftStyle: "outset", paddingLeft: "10px" }} title={localTooltip}>
                <small className="text-muted">{localSummary}</small>
              </div>
            )}
          </Accordion.Item>
        );
      })}
    </Accordion>
  );

  const runPreviewProcess = (item, key, file) => {
    // eslint-disable-next-line no-console
    // console.log("PREVIEW_PROCESS_ITEM", item.fileName);
    setLoadingPreviewKey(key);
    previewXlsxSkirts(file, loggedUser.username).then(({ data, error }) => {
      if (error) throw new Error(error?.message);
      var url = window.URL.createObjectURL(new Blob([data], { type: 'application/pdf' }));
      setPreviewUrl(url);
      setPreviewKey(key);
    }).catch(() => {
      // No-op: keep silent, UI will just stop loading
    }).finally(() => {
      setLoadingPreviewKey(null);
    });
    // setPreviewUrl(TestPdf);
    // setPreviewKey(key);
  };

  const renderStep3 = () => (
    <Accordion
      alwaysOpen
      onSelect={(ek) => {
        if (ek !== previewKey) {
          setPreviewKey(null);
          setPreviewUrl(null);
        }
      }}
    >
      {parsed.map((p, idx) => (
        <Accordion.Item eventKey={String(idx)} key={`prev-${p.fileName}`}>
          <Accordion.Header>{`${p.fileName} (${p.header.campaign || "N/A"})`}</Accordion.Header>
          <Accordion.Body>
            <div className="d-flex align-items-center justify-content-between">
              <div>Listo para previsualizar.</div>
              {!(previewKey === String(idx) && Boolean(previewUrl)) && (
                <Button variant="outline-secondary" size="sm" onClick={() => runPreviewProcess(p, String(idx), filesToUpload[idx])} disabled={loadingPreviewKey === String(idx)}>
                  Previsualizar
                </Button>
              )}
            </div>
            {loadingPreviewKey === String(idx) && (
              <div className="mt-3 d-flex align-items-center justify-content-center">
                <SmuLoading label="Generando previsualización..." />
              </div>
            )}
            {previewKey === String(idx) && Boolean(previewUrl) && (
              <div className="mt-3">
                <SmuPdfPreview pdf={previewUrl} allowPrint={false} />
              </div>
            )}
          </Accordion.Body>
        </Accordion.Item>
      ))}
    </Accordion>
  );

  const handleCancel = () => {
    onHide();
  };

  const handleUploadProcessModal = () => {
    // Create dictionary of faldon_version_json
    var faldon_version_json_dict = {};
    parsed.forEach((p) => {
      const key = getCampaignFormatKey(p?.header?.campaign, p?.header?.format, p?.fileName);
      faldon_version_json_dict[p.fileName] = comparedRowsByCampaign[key] || [];
    });
    return handleUploadProcess(faldon_version_json_dict);
  }

  const handleNext = () => setActiveStep((s) => Math.min(2, s + 1));
  const handleBack = () => setActiveStep((s) => Math.max(0, s - 1));

  const handleConfirm = () => {
    // eslint-disable-next-line no-console
    // TODO EXECUTE UPLOAD PROCESS
    handleUploadProcessModal();
    onHide();
  };

  return (
    <Modal show={show} onHide={onHide} size="xl" centered scrollable height="80vh" backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title>
          {activeStep === 0 && "Datos generales"}
          {activeStep === 1 && "Vista documento"}
          {activeStep === 2 && "Previsualización"}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <center><Stepper active={activeStep} /></center>
        {activeStep === 0 && renderStep1()}
        {activeStep === 1 && renderStep2()}
        {activeStep === 2 && renderStep3()}
      </Modal.Body>
      <div className="d-flex align-items-center justify-content-between px-3 pb-3">
        <Button variant="secondary" onClick={handleCancel}>Cancelar Carga</Button>
        <div className="d-flex gap-2">
          {activeStep > 0 && (
            <Button variant="secondary" onClick={handleBack}>Atrás</Button>
          )}
          {activeStep < 2 && (
            activeStep === 1 ? (
              <Button class="btn smu-generic-button solid-btn" onClick={handleNext} disabled={loadingCampaigns}>Siguiente</Button>
            ) : (
              <Button class="btn smu-generic-button solid-btn" onClick={handleNext} disabled={!canContinue}>Siguiente</Button>
            )
          )}
          {activeStep === 2 && (
            <Button class="btn smu-generic-button solid-btn" onClick={handleConfirm}>{parsed.length > 1 ? "Cargar Archivos" : "Cargar Archivo"}</Button>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default PreviewXlsxModal;


